import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IInventoryItem, IInventoryItemInput, inventoryItemsAtom, inventoryItemsLoadingAtom } from "./types";

type UseInventoryItems = [IInventoryItem[], {
    list: (query?: Record<string, any>) => Promise<IInventoryItem[] | null>;
    getById: (id: number) => Promise<IInventoryItem | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IInventoryItem[] | null>;
    create: (input: IInventoryItemInput) => Promise<IInventoryItem | null>;
    update: (id: number, input: IInventoryItemInput) => Promise<IInventoryItem | null>;
    remove: (id: number) => Promise<IInventoryItem | null>;
    updateQuantity: (item_id: number, args: { delta?: number, absoluteQuantity?: number, actor?: number }) => Promise<IInventoryItem | null>;
    moveToZone: (item_id: number, zone_id: number, actor?: number) => Promise<IInventoryItem | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;

const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, {
        headers: { 'X-Wallmates-Auth': authToken }
    });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useInventoryItems = (): UseInventoryItems => {
    const [items, setItems] = useAtom(inventoryItemsAtom);
    const setLoading = useSetAtom(inventoryItemsLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try {
            setLoading(true);
            const q = `query($query: JSON){ getInventoryItems(query: $query){ id trackable_item_id purchase_order_id serial barcode zone_id unit_count stock_size_units status created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getInventoryItems);
            return data.getInventoryItems as IInventoryItem[];
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to fetch inventory items.' });
            return null;
        } finally { setLoading(false); }
    };

    const getById = async (id: number) => {
        try {
            const q = `query($id: ID!){ getInventoryItemById(id: $id){ id trackable_item_id purchase_order_id serial barcode zone_id unit_count stock_size_units status created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getInventoryItemById as IInventoryItem;
        } catch (err: any) { errorCatcher(err, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try {
            const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedInventoryItems(first:$first,offset:$offset,query:$query){ id trackable_item_id purchase_order_id serial barcode zone_id unit_count stock_size_units status created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedInventoryItems as IInventoryItem[];
        } catch (err: any) { errorCatcher(err, loginFunctions.logout); return null; }
    };

    const create = async (input: IInventoryItemInput) => {
        try {
            const q = `mutation($input: InventoryItemInput!){ createInventoryItem(input:$input){ id trackable_item_id purchase_order_id serial barcode zone_id unit_count stock_size_units status created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createInventoryItem as IInventoryItem;
        } catch (err: any) { errorCatcher(err, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IInventoryItemInput) => {
        try {
            const q = `mutation($id:ID!,$input:InventoryItemInput!){ updateInventoryItem(id:$id,input:$input){ id trackable_item_id purchase_order_id serial barcode zone_id unit_count stock_size_units status created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateInventoryItem as IInventoryItem;
        } catch (err: any) { errorCatcher(err, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try {
            const q = `mutation($id:ID!){ deleteInventoryItem(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteInventoryItem as IInventoryItem;
        } catch (err: any) { errorCatcher(err, loginFunctions.logout); return null; }
    };

    const updateQuantity = async (item_id: number, args: { delta?: number, absoluteQuantity?: number, actor?: number }) => {
        try {
            const q = `mutation($item_id:ID!, $delta:Int, $absoluteQuantity:Int, $actor:ID){ updateInventoryItemQuantity(item_id:$item_id, delta:$delta, absoluteQuantity:$absoluteQuantity, actor:$actor){ id unit_count zone_id status } }`;
            const data = await graphqlRequest(authToken, q, { item_id, ...args });
            return data.updateInventoryItemQuantity as IInventoryItem;
        } catch (err: any) { errorCatcher(err, loginFunctions.logout); return null; }
    };

    const moveToZone = async (item_id: number, zone_id: number, actor?: number) => {
        try {
            const q = `mutation($item_id:ID!, $zone_id:ID!, $actor:ID){ moveInventoryItemToZone(item_id:$item_id, zone_id:$zone_id, actor:$actor){ id zone_id } }`;
            const data = await graphqlRequest(authToken, q, { item_id, zone_id, actor });
            return data.moveInventoryItemToZone as IInventoryItem;
        } catch (err: any) { errorCatcher(err, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove, updateQuantity, moveToZone }];
}


